import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Typography,
    Alert
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import NotificationService from 'api/NotificationService';
import { gridSpacing } from 'store/constant';
import { toast } from 'react-toastify';

const SendNotificationModal = ({ open, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            title: '',
            message: '',
            target: 'all_vendors',
            identifier: '',
            channels: ['in_app', 'push']
        },
        validationSchema: Yup.object({
            title: Yup.string().required('Title is required'),
            message: Yup.string().required('Message is required'),
            target: Yup.string(),
            identifier: Yup.string().when('target', {
                is: (target) => !target,
                then: Yup.string().required('Email or Phone is required for specific users')
            })
        }),
        onSubmit: async (values, { setSubmitting, setStatus }) => {
            setLoading(true);
            try {
                const payload = { ...values };
                if (!payload.target) delete payload.target;
                if (!payload.identifier) delete payload.identifier;

                const response = await NotificationService.sendNotification(payload);
                
                toast.success(response.message || 'Notification Sent Successfully');
                
                setLoading(false);
                if (onSuccess) onSuccess();
                onClose();
                formik.resetForm();
            } catch (error) {
                setLoading(false);
                setStatus({ success: false });
                const errorMsg = error.response?.data?.message || 'Failed to send notification';
                toast.error(errorMsg);
                console.error('Failed to send notification', error);
            }
        }
    });

    const handleChannelChange = (channel) => {
        const current = formik.values.channels;
        const next = current.includes(channel) 
            ? current.filter(c => c !== channel) 
            : [...current, channel];
        formik.setFieldValue('channels', next);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Send System Notification</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Stack spacing={gridSpacing}>
                        <TextField
                            fullWidth
                            id="title"
                            name="title"
                            label="Title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title && formik.errors.title}
                        />
                        <TextField
                            fullWidth
                            id="message"
                            name="message"
                            label="Message"
                            multiline
                            rows={3}
                            value={formik.values.message}
                            onChange={formik.handleChange}
                            error={formik.touched.message && Boolean(formik.errors.message)}
                            helperText={formik.touched.message && formik.errors.message}
                        />
                        
                        <FormControl fullWidth>
                            <InputLabel id="target-label">Target Audience</InputLabel>
                            <Select
                                labelId="target-label"
                                id="target"
                                name="target"
                                value={formik.values.target}
                                label="Target Audience"
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="all_vendors">All Vendors</MenuItem>
                                <MenuItem value="all_riders">All Riders</MenuItem>
                                <MenuItem value="all_customers">All Customers</MenuItem>
                                <MenuItem value="all_admins">All Admins</MenuItem>
                                <MenuItem value="">Specific User (Email/Phone)</MenuItem>
                            </Select>
                        </FormControl>

                        {formik.values.target === '' && (
                            <TextField
                                fullWidth
                                id="identifier"
                                name="identifier"
                                label="User Email or Phone Number"
                                placeholder="e.g. user@example.com or +234..."
                                value={formik.values.identifier}
                                onChange={formik.handleChange}
                                error={formik.touched.identifier && Boolean(formik.errors.identifier)}
                                helperText={formik.touched.identifier && formik.errors.identifier}
                            />
                        )}

                        <Stack spacing={1}>
                            <Typography variant="subtitle2">Delivery Channels</Typography>
                            <FormGroup row>
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.channels.includes('in_app')} onChange={() => handleChannelChange('in_app')} />}
                                    label="In-App"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.channels.includes('push')} onChange={() => handleChannelChange('push')} />}
                                    label="Push Notification"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.channels.includes('email')} onChange={() => handleChannelChange('email')} />}
                                    label="Email"
                                />
                            </FormGroup>
                            {formik.values.channels.length === 0 && (
                                <Alert severity="warning">Please select at least one delivery channel.</Alert>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={onClose} disabled={loading} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || formik.values.channels.length === 0}
                        sx={{ minWidth: 100 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SendNotificationModal;
